import { isImageUrl } from '@findigs/helpers/src/helpers/is-image-url';
import { useNodeRefAndForwardIt } from '@findigs/helpers/src/hooks/use-node-ref-and-forward-it';
import React, { useCallback, useEffect, useState } from 'react';

export type RemoteImageProps = Omit<
  React.HTMLProps<HTMLImageElement>,
  'ref' | 'as'
> & {
  /** @default false */
  disableLoadAnimation?: boolean;
  nonPreviewableFallback?: React.ReactNode;
  loading?: 'eager' | 'lazy';
};

const options: KeyframeAnimationOptions = {
  duration: 700, //ms
  iterations: 1,
  easing: 'ease',
};

const keyframes = [
  {
    opacity: 0,
  },
  {
    opacity: 1,
  },
];

/**
 * Extends the vanilla `img` element with custom behavior.
 * All vanilla `img` props are forwarded.
 *
 * This is a drop-in replacement for the `img` tag with a few
 * improvements, such as support for fade-in animations on load
 */
export const RemoteImage = React.forwardRef(
  (
    {
      disableLoadAnimation,
      src,
      nonPreviewableFallback = null,
      onError: forwardedOnError,
      ...props
    }: RemoteImageProps,
    forwardedRef: React.ForwardedRef<HTMLImageElement>
  ) => {
    const [callbackRef, ref] = useNodeRefAndForwardIt(forwardedRef);
    const [failed, setFailed] = useState(false);

    const onError = useCallback(
      (e: React.SyntheticEvent<HTMLImageElement>) => {
        setFailed(true);
        forwardedOnError?.(e);
      },
      [forwardedOnError]
    );

    useEffect(() => {
      setFailed(false);

      const img = ref.current;

      const animation = img?.animate(keyframes, {
        ...options,
        ...(disableLoadAnimation ? { duration: 1, easing: 'linear' } : {}),
      });
      animation?.pause();

      let play = false;
      const onLoad = () => {
        if (!play && src && img?.complete) {
          play = true;
          animation?.play();
        }
      };

      onLoad();
      img?.addEventListener('load', onLoad);
      const timer = setTimeout(onLoad, 50);

      return () => {
        img?.removeEventListener('load', onLoad);
        clearTimeout(timer);
        animation?.cancel();
      };

      // we are intentionally ignoring the `disableLoadAnimation` dependency
      // as we don't want to re-run the effect when it changes

      // eslint-disable-next-line @grncdr/react-hooks/exhaustive-deps
    }, [src]);

    if (nonPreviewableFallback && (!src || isImageUrl(src) === false || failed))
      return nonPreviewableFallback;

    return <img {...props} src={src} ref={callbackRef} onError={onError} />;
  }
);
RemoteImage.displayName = 'RemoteImage';
